import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { EMPTY, Observable } from "rxjs";
import { AsyncStorage } from "@core/services/AsyncStorage";
import { ClientsService, Client } from "@admin/clients/clients.service";
import { APIResponse } from "@shared/types/APIResponse";
import { DebugService as debug } from "@core/services/debug.service";
import { WhiteboxLog } from "@core/data/whiteboxLog";
import { Whitebox } from "@core/data/controller";
import { expand, map, reduce } from "rxjs/operators";
import { Export } from "@shared/lib/Export";
import { AuthService } from "@core/services/auth/auth.service";

export { Whitebox };

interface WhiteboxListOutput {
  devices: Whitebox[];
  lastEvaluatedKey: string | null;
}

@Injectable()
export class WhiteboxLogService {
  private _activeClientId: string;
  private _whiteboxes: AsyncStorage<Whitebox>;

  constructor(
    private _http: HttpClient,
    private _clientsService: ClientsService,
    private _auth: AuthService
  ) {
    this._initializeStorage();
  }

  private _initializeStorage() {
    //Prepare location storage
    this._whiteboxes = new AsyncStorage(this._http);
    this._whiteboxes._setElementComparisonFunction(this._whiteboxComparison);
    this._whiteboxes._setUpdateFunction(this._updateWhitebox());

    this._clientsService.clients
      .onActiveElementChange()
      .subscribe((client: Client) => {
        this._activeClientId = client.clientId;
        this._whiteboxes.update();
      });
  }

  public get whiteboxes() {
    return this._whiteboxes;
  }

  private _whiteboxComparison(a: Whitebox, b: Whitebox): boolean {
    //Compare the two
    if (a == null || b == null) return false;
    if (a.whiteboxId && b.whiteboxId) return a.whiteboxId === b.whiteboxId;
    return a.macAddress === b.macAddress;
  }

  private _updateWhitebox(): () => Observable<Whitebox[]> {
    let self = this;

    return () => {
      debug.log("Updating whiteboxes accounts");

      const clientId = self._activeClientId || self._auth.activeClient?.clientId;

      return this._http
        .get<APIResponse<WhiteboxListOutput>>("whitebox/devices", {
          params: { clientId },
        })
        .pipe(
          expand((response) => {
            if (response?.errors?.length) throw new Error(response.errors[0]);

            const lastEvaluatedKey = response?.data?.lastEvaluatedKey;
            if (!lastEvaluatedKey) return EMPTY;

            return this._http.get<APIResponse<WhiteboxListOutput>>(
              "whitebox/devices",
              {
                params: { clientId, lastEvaluatedKey },
              }
            );
          }),
          map((response) => response?.data?.devices || []),
          reduce(
            (allWhiteboxes, currentWhiteboxes) =>
              allWhiteboxes.concat(currentWhiteboxes),
            [] as Whitebox[]
          ),
          map((whiteboxes: Whitebox[]) => {
            //Sort alphabetically
            whiteboxes.sort(
              (leftSide: Whitebox, rightSide: Whitebox): number => {
                if (leftSide.nickname < rightSide.nickname) return -1;
                if (leftSide.nickname > rightSide.nickname) return 1;
                return 0;
              }
            );

            return whiteboxes;
          })
        );
    };
  }

  public async getLogs(macAddress, startDate, endDate): Promise<WhiteboxLog[]> {
    let data = null;

    try {
      data = await this._http
        .post("cwfController/getLogs", {
          macAddress: macAddress,
          startTimeStamp: startDate,
          endTimeStamp: endDate,
          payload: true,
        })
        .toPromise();
    } finally {
      // empty
    }

    if (!data?.data) return []

    const logs = Array.isArray(data?.data) ? data?.data : [data?.data]
    return logs.map((log: WhiteboxLog) => {

      //Parse payload for improved temporal sorting
      try {
        if ("string" === typeof log?.payload)
          log.parsedPayload = JSON.parse(log.payload)
      } catch (e) {
        debug.error(e);
      }

      //Set status to lowercase for consistency
      if ("string" === typeof log?.status)
        log.status = log.status.toLowerCase()

      return log;
    })

  }

  public async exportCsv(macAddress, startDate, endDate): Promise<void> {
    const data = await this._http
      .post(
        "cwfController/getLogsCsv",
        {
          macAddress: macAddress,
          startTimeStamp: startDate,
          endTimeStamp: endDate,
          payload: true,
        },
        { responseType: "arraybuffer" }
      )
      .toPromise();

    Export.csv(data, "whitebox-logs.csv");
  }
}
